import React, { useState } from 'react';
import { FaSearch, FaBan } from 'react-icons/fa';
import Navbar from '../navbar/navbar';
import { cancelReceipt, fetchReceiptDetails, formatDate } from '../../services/receipt.service';
import Swal from 'sweetalert2';
import AlertConfirmation from '../../common/AlertConfirmation.component';

const CancelReceiptComponent = () => {
    const [receiptNo, setReceiptNo] = useState('');
    const [receiptDetails, setReceiptDetails] = useState(null);

    const paymentTypes = {
        'CA': 'Cash',
        'CQ': 'Cheque',
        'DC': 'Card',
        'RT': 'RTGS'
    }

    const handleSearch = async () => {
        if (!receiptNo.trim()) {
            Swal.fire('Error', 'Please enter a receipt number.', 'warning');
            return;
        }
        try {
            const response = await fetchReceiptDetails(receiptNo.trim());

            if (response && response.receiptNo) {
                setReceiptDetails(response);
            } else {
                setReceiptDetails(null);
                Swal.fire('Not Found', 'No receipt found with this number.', 'error');
            }
        } catch (error) {
            console.error(error);
            Swal.fire('Error', 'Something went wrong while fetching data.', 'error');
        }
    };

    const handleCancel = async () => {
        if (!receiptDetails?.receiptNo) return;

        const confirm = await AlertConfirmation({ title: "Are you sure?", message: `Do you really want to cancel receipt ${receiptDetails.receiptNo}?` });
        if (confirm) {
            try {
                const response = await cancelReceipt(receiptDetails.receiptId);
                if (response > 0) {
                    Swal.fire('Cancelled', `Receipt ${receiptDetails.receiptNo} cancelled successfully.`, 'success');
                    setReceiptDetails(null);
                    setReceiptNo('');
                } else {
                    Swal.fire('Error', 'Failed to cancel the receipt.', 'error');
                }
            } catch (error) {
                console.error(error);
                Swal.fire('Error', 'Failed to cancel the receipt.', 'error');
            }
        }
    };

    return (
        <div className="card">
            <Navbar />
            <div className="card-body">
                <div className="row">
                    <div className="col-md-12 text-center">
                        <h3 className="admin-custom-heading">Cancel Receipt</h3>
                    </div>
                </div>
            </div>
            <div className="container py-4">
                <div className="card shadow-sm p-4">
                    <div className="row g-2 align-items-center mb-3">
                        <div className="col-sm-3"></div>
                        <div className="col-sm-5">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Enter Receipt No"
                                value={receiptNo}
                                onChange={(e) => setReceiptNo(e.target.value)}
                                maxLength={15}
                            />
                        </div>
                        <div className="col-sm-2 d-grid">
                            <button className="btn btn-primary" onClick={handleSearch}>
                                <FaSearch className="me-1" /> Search
                            </button>
                        </div>
                    </div>
                    {receiptDetails && (
                        <div className="mt-4">
                            <div className="card border-primary shadow-sm mb-3">
                                <div className="card-header bg-primary text-white">
                                    <h5 className="mb-0">Receipt Preview</h5>
                                </div>
                                <div className="card-body">
                                    {/* Section 1: Receipt Info */}
                                    <div className="row row-cols-1 row-cols-md-2 gy-2 gx-4 mb-3">
                                        <div><strong>Receipt No:</strong> {receiptDetails.receiptNo}</div>
                                        <div><strong>Name:</strong> {receiptDetails.devoteeName}</div>
                                        <div><strong>Mobile:</strong> {receiptDetails.mobile}</div>
                                        <div><strong>Gotra:</strong> {receiptDetails.gotra}</div>
                                        <div><strong>Nakshatra:</strong> {receiptDetails.nakshatra}</div>
                                        <div><strong>Payment Mode:</strong> {paymentTypes[receiptDetails.paymentType]}</div>
                                        <div><strong>Total Person:</strong> {receiptDetails.totalPerson}</div>
                                        <div><strong>Total Amount:</strong> ₹{receiptDetails.totalAmount}</div>
                                    </div>

                                    {/* Section 2: Seva Info */}
                                    <div className="bg-light border rounded p-3 mb-3">
                                        <h6 className="text-secondary fw-bold mb-3">Seva Details</h6>
                                        <div className="row row-cols-1 row-cols-md-2 gy-2 gx-4">
                                            <div><strong>Seva Name 1:</strong> {receiptDetails.poojaName}</div>
                                            <div><strong>Seva Date 1:</strong> {formatDate(receiptDetails.poojaDateTime1)}</div>
                                            {receiptDetails.poojaName2 && receiptDetails.poojaDateTime2 && (
                                                <>
                                                    <div><strong>Seva Name 2:</strong> {receiptDetails.poojaName2}</div>
                                                    <div><strong>Seva Date 2:</strong> {formatDate(receiptDetails.poojaDateTime2)}</div>
                                                </>
                                            )}
                                        </div>
                                    </div>

                                    {/* Section 3: Payment Info */}
                                    {receiptDetails.paymentDetails && Object.keys(receiptDetails.paymentDetails).length > 0 && receiptDetails.paymentType !== 'CA' && (
                                        <div className="bg-light border rounded p-3">
                                            <h6 className="text-secondary fw-bold mb-3">Payment Details</h6>
                                            {receiptDetails.paymentDetails.chequeNo && (
                                                <div className="row row-cols-1 row-cols-md-3 gy-2 gx-4">
                                                    <div><strong>Cheque No:</strong> {receiptDetails.paymentDetails.chequeNo}</div>
                                                    <div>
                                                        <strong>Cheque Date:</strong>{' '}
                                                        {receiptDetails.paymentDetails.chequeDate
                                                            ? new Date(receiptDetails.paymentDetails.chequeDate).toLocaleDateString('en-GB').replace(/\//g, '-')
                                                            : ''}
                                                    </div>
                                                    <div><strong>Bank Name:</strong> {receiptDetails.paymentDetails.bankName}</div>
                                                </div>
                                            )}
                                            {receiptDetails.paymentDetails.cardNo && (
                                                <div className="row row-cols-1 row-cols-md-2 gy-2 gx-4">
                                                    <div><strong>Card No:</strong> {receiptDetails.paymentDetails.cardNo}</div>
                                                    <div><strong>Card Type:</strong> {receiptDetails.paymentDetails.cardType}</div>
                                                </div>
                                            )}
                                            {receiptDetails.paymentDetails.rtgsNo && (
                                                <div className="row row-cols-1 row-cols-md-2 gy-2 gx-4">
                                                    <div><strong>RTGS No:</strong> {receiptDetails.paymentDetails.rtgsNo}</div>
                                                    <div><strong>Bank Name:</strong> {receiptDetails.paymentDetails.bankName}</div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Cancel Button */}
                            <div className="text-end">
                                <button className="btn btn-danger px-4" onClick={handleCancel}>
                                    <FaBan className="me-1" /> Cancel Receipt
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CancelReceiptComponent;
